import { useMemo, useState, useEffect, useRef } from "react";
import tokenService, {
  AUTH_TOKENS_CHANGED_EVENT,
} from "services/token.service";
import { useAccount } from "wagmi";

// Global state to track authentication attempts
let globalAuthInProgress = false;
let globalAuthInProgressTimeout: ReturnType<typeof setTimeout> | null = null;
const AUTH_IN_PROGRESS_CHANGED_EVENT = "authInProgressChanged";

const notifyAuthInProgressChange = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(AUTH_IN_PROGRESS_CHANGED_EVENT, {
        detail: globalAuthInProgress,
      })
    );
  }
};

export const setAuthInProgress = (inProgress: boolean) => {
  globalAuthInProgress = inProgress;
  notifyAuthInProgressChange();

  // Auto-reset auth in progress after 30 seconds to prevent permanent blocking
  if (inProgress) {
    if (globalAuthInProgressTimeout) {
      clearTimeout(globalAuthInProgressTimeout);
    }
    globalAuthInProgressTimeout = setTimeout(() => {
      globalAuthInProgress = false;
      globalAuthInProgressTimeout = null;
      notifyAuthInProgressChange();
    }, 30000);
  } else {
    if (globalAuthInProgressTimeout) {
      clearTimeout(globalAuthInProgressTimeout);
      globalAuthInProgressTimeout = null;
    }
  }
};

export const useAuthInProgress = () => {
  const [authInProgress, setAuthInProgressState] = useState(
    globalAuthInProgress
  );

  useEffect(() => {
    const handleAuthInProgressChange = (event: Event) => {
      setAuthInProgressState((event as CustomEvent<boolean>).detail);
    };

    window.addEventListener(
      AUTH_IN_PROGRESS_CHANGED_EVENT,
      handleAuthInProgressChange
    );

    return () => {
      window.removeEventListener(
        AUTH_IN_PROGRESS_CHANGED_EVENT,
        handleAuthInProgressChange
      );
    };
  }, []);

  return authInProgress;
};

export const useAuth = () => {
  const { address, isConnected } = useAccount();
  const [hasValidToken, setHasValidToken] = useState(false);
  const authInProgress = useAuthInProgress();
  const lastAddressRef = useRef<string>("");

  useEffect(() => {
    const checkToken = () => {
      // Reset authentication state when address changes
      if (lastAddressRef.current !== address) {
        lastAddressRef.current = address || "";
        setHasValidToken(false);
        // Reset global auth progress when address changes
        setAuthInProgress(false);
      }

      // If no address or not connected, user is not authenticated
      if (!address || !isConnected) {
        setHasValidToken(false);
        return;
      }

      // Check if we have tokens
      const tokens = tokenService.tokens;
      const currentAddress = address.toLowerCase();
      const tokenAddress = tokens?.user?.address?.toLowerCase();

      if (tokens && tokenAddress === currentAddress) {
        // We have tokens for the connected wallet, consider user authenticated
        setHasValidToken(true);
        // Reset auth in progress since we have valid tokens
        setAuthInProgress(false);
      } else {
        // No tokens, user needs to authenticate
        setHasValidToken(false);
      }
    };

    checkToken();

    // Listen for storage changes to update auth state
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "authTokens" || e.key === null) {
        checkToken();
      }
    };
    const handleTokenChange = () => checkToken();

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(AUTH_TOKENS_CHANGED_EVENT, handleTokenChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(AUTH_TOKENS_CHANGED_EVENT, handleTokenChange);
    };
  }, [address, isConnected]);

  // Return true only if we have address, connection, valid token, and no global auth in progress
  return useMemo(() => {
    return Boolean(address && isConnected && hasValidToken && !authInProgress);
  }, [address, isConnected, hasValidToken, authInProgress]);
};
